import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { megaMenuColumns, primaryProducts } from '~/content/navigation';
import type { LinkItem } from '~/content/types';
import { cn } from '~/lib/cn';

interface Category {
  label: string;
  links: LinkItem[];
}

// Same visual weight as Products / Tools, but navigates directly (no submenu).
const primaryDirectLinks: LinkItem[] = [{ label: 'Business', href: '/business' }];

// Flat links shown below the categories (no submenu).
const secondaryLinks: LinkItem[] = [
  { label: 'Pricing', href: '/pricing' },
  { label: 'Learn', href: '/help' },
  { label: 'About us', href: '/about' },
];

// Staggered entrance (matches the desktop mega menu): each item rises 20px and
// fades in, cascading by index. On exit the delay drops so everything hides at
// once (no reverse cascade).
const RISE_MS = 420;
const RISE_STEP = 40;
// Panel reveal: the drawer masks open horizontally via an animated clip-path
// that expands in from the right edge (inset 100% → 0). Content stays put and
// is revealed by the clip (a true mask, not a translate/slide).
const MASK_MS = 420;

function rise(
  active: boolean,
  index: number,
  base: number,
): {
  className: string;
  style: CSSProperties;
} {
  const delay = active ? `${base + index * RISE_STEP}ms` : '0ms';
  return {
    className: active ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
    style: {
      transitionProperty: 'opacity, translate',
      transitionTimingFunction: 'var(--ease-nav)',
      transitionDuration: `${RISE_MS}ms, ${RISE_MS}ms`,
      transitionDelay: `${delay}, ${delay}`,
    },
  };
}

/**
 * Fullscreen mobile drawer with two levels (Figma "Menu_MobileNew" 209:7228).
 * Controlled by `open` (like the desktop mega menu): the drawer masks open
 * horizontally (clip-path revealing right → left) and the items cascade in.
 * Tapping a category slides the main list out to the left and its submenu in
 * from the right.
 */
export function MobileNav({
  open,
  onClose,
  stablecoinAvailable,
}: {
  open: boolean;
  onClose: () => void;
  stablecoinAvailable: boolean;
}) {
  // Products reuses the desktop list, filtered before rendering so the
  // restricted link is absent from both the initial HTML and the live DOM.
  const products = stablecoinAvailable
    ? primaryProducts
    : primaryProducts.filter((item) => item.href !== '/stablecoin');
  const categories: Category[] = [
    { label: 'Products', links: products },
    ...megaMenuColumns.map((column) => ({ label: column.title, links: column.links })),
  ];

  // `shown` is the category rendered in the submenu pane; it persists through
  // the back animation so the content doesn't vanish mid-slide.
  const [shown, setShown] = useState<Category | null>(null);
  const [subOpen, setSubOpen] = useState(false);

  // Lock body scroll while open; reset to the top level whenever it closes.
  useEffect(() => {
    if (!open) {
      setSubOpen(false);
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const back = () => setSubOpen(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (subOpen) setSubOpen(false);
      else onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subOpen]);

  const openCategory = (category: Category) => {
    // Mount the submenu content first, then flip `subOpen` on the next frame so
    // the track slides over to reveal the submenu pane.
    setShown(category);
    requestAnimationFrame(() => setSubOpen(true));
  };

  return (
    <div className={cn('fixed inset-0 z-[60] lg:hidden', !open && 'pointer-events-none')}>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-blur-backdrop/60 backdrop-blur-sm transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />

      <div
        className="absolute inset-y-0 right-0 w-[86%] max-w-[420px] bg-white transition-[clip-path] ease-nav"
        style={{
          transitionDuration: `${MASK_MS}ms`,
          clipPath: open ? 'inset(0 0 0 0)' : 'inset(0 0 0 100%)',
        }}>
        <div className="flex h-dvh flex-col">
          {/* Top bar: Back appears in a submenu (Close lives in the header toggle). */}
          <div className="flex h-16 shrink-0 items-center px-5">
            <button
              type="button"
              onClick={back}
              aria-label="Back"
              className={cn(
                'flex items-center gap-2 text-body font-medium text-ink transition-opacity duration-200',
                subOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}>
              <svg
                viewBox="0 0 24 24"
                className="size-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                <path d="M15 5l-7 7 7 7" strokeLinecap="butt" strokeLinejoin="miter" />
              </svg>
              Back
            </button>
          </div>

          {/* Sliding track: two full-width panes side by side. */}
          <div className="relative flex-1 overflow-hidden">
            <div
              className={cn(
                'flex h-full w-[200%] transition-transform duration-300 ease-nav',
                subOpen ? '-translate-x-1/2' : 'translate-x-0',
              )}>
              {/* Main view */}
              <div className="flex h-full w-1/2 shrink-0 flex-col overflow-y-auto px-5 pt-6 pb-10">
                <nav aria-label="Categories" className="flex flex-col gap-1">
                  {categories.map((category, i) => {
                    const anim = rise(open, i, 60);
                    return (
                      <button
                        key={category.label}
                        type="button"
                        onClick={() => openCategory(category)}
                        style={anim.style}
                        className={cn(
                          'flex items-center justify-between py-2 text-h4 font-semibold text-ink',
                          anim.className,
                        )}>
                        {category.label}
                        <svg
                          viewBox="0 0 24 24"
                          className="size-6 text-ink"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2">
                          <path d="M9 5l7 7-7 7" strokeLinecap="butt" strokeLinejoin="miter" />
                        </svg>
                      </button>
                    );
                  })}
                  {primaryDirectLinks.map((link, i) => {
                    const anim = rise(open, categories.length + i, 60);
                    return (
                      <Link
                        key={link.label}
                        to={link.href}
                        onClick={onClose}
                        style={anim.style}
                        className={cn(
                          'flex items-center justify-between py-2 text-h4 font-semibold text-ink',
                          anim.className,
                        )}>
                        {link.label}
                        <svg
                          viewBox="0 0 24 24"
                          className="size-6 text-ink"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2">
                          <path d="M9 5l7 7-7 7" strokeLinecap="butt" strokeLinejoin="miter" />
                        </svg>
                      </Link>
                    );
                  })}
                </nav>

                <nav aria-label="More" className="mt-8 flex flex-col gap-1">
                  {secondaryLinks.map((link, i) => {
                    const anim = rise(open, categories.length + primaryDirectLinks.length + i, 60);
                    const className = cn('py-2 text-h5 font-semibold text-ink', anim.className);
                    return link.href === '/help' ? (
                      <a
                        key={link.label}
                        href={link.href}
                        onClick={onClose}
                        style={anim.style}
                        className={className}>
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.href}
                        onClick={onClose}
                        style={anim.style}
                        className={className}>
                        {link.label}
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Submenu view */}
              <div className="flex h-full w-1/2 shrink-0 flex-col overflow-y-auto px-5 pt-6 pb-10">
                <nav aria-label={shown?.label} className="flex flex-col">
                  {shown?.links.map((link) =>
                    link.external ? (
                      <a
                        key={link.label}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                        className="py-3 text-h5 font-semibold text-ink">
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.href}
                        onClick={onClose}
                        className="py-3 text-h5 font-semibold text-ink">
                        {link.label}
                      </Link>
                    ),
                  )}
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
